import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where
} from 'firebase/firestore'
import { db } from '../../firebase'
import { IconPickerItem } from '../Form/IconField'
import PaginatedTable from '../PaginatedTable'
import Spinner from '../Spinner'

export default function UsersList({ organizationId }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [pageCursors, setPageCursors] = useState([])

  const [opts, setOpts] = useState({
    itemsPerPage: 10,
    sortBy: 'email', // must exist in your documents
    sortOrder: 'asc',
    persistent: true,
    uid: `users-list-${organizationId}`
  })

  const columns = [
    {
      name: 'Name',
      sortKey: 'email',
      render: (item) => item?.displayName
    },
    {
      name: 'Email',
      sortKey: 'email',
      render: (item) => item.email
    },
    {
      name: 'User Type',
      render: (item) =>
        item.userType ? (
          <IconPickerItem
            icon={item.userType.icon}
            size={24}
            color={item.userType.color}
          />
        ) : (
          'N/A'
        )
    }
  ]

  // Reset pagination when org ID changes
  useEffect(() => {
    const preloadCursors = async () => {
      const usersRef = collection(db, 'users')
      const fullQuery = query(
        usersRef,
        where('organization.id', '==', organizationId),
        orderBy(opts.sortBy, opts.sortOrder)
      )

      const snapshot = await getDocs(fullQuery)
      const docs = snapshot.docs

      const cursors = []
      for (
        let i = opts.itemsPerPage - 1;
        i < docs.length;
        i += opts.itemsPerPage
      ) {
        cursors.push(docs[i]) // store last document per page
      }

      setPageCursors(cursors)
      setTotalCount(docs.length)
    }

    if (organizationId) {
      preloadCursors()
      setCurrentPage(0)
    }
  }, [organizationId, opts.sortBy, opts.sortOrder, opts.itemsPerPage])

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true)
      try {
        const usersRef = collection(db, 'users')

        let baseQuery = query(
          usersRef,
          where('organization.id', '==', organizationId),
          orderBy(opts.sortBy, opts.sortOrder),
          limit(opts.itemsPerPage)
        )

        if (currentPage > 0 && pageCursors[currentPage - 1]) {
          baseQuery = query(
            usersRef,
            where('organization.id', '==', organizationId),
            orderBy(opts.sortBy, opts.sortOrder),
            startAfter(pageCursors[currentPage - 1]),
            limit(opts.itemsPerPage)
          )
        }

        const snapshot = await getDocs(baseQuery)

        const users = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        setRows(users)
      } catch (error) {
        console.error('Error fetching users:', error)
      } finally {
        setLoading(false)
      }
    }

    if (organizationId) {
      fetchUsers()
    }
  }, [
    currentPage,
    pageCursors,
    organizationId,
    opts.sortBy,
    opts.sortOrder,
    opts.itemsPerPage
  ])

  const setPage = (page) => setCurrentPage(page)
  const setOptions = (newOpts) => {
    setOpts((prev) => ({ ...prev, ...newOpts }))
    setCurrentPage(0) // reset page when options change
    setPageCursors([])
  }

  if (loading) return <Spinner />

  return (
    <PaginatedTable
      className='is-fullwidth is-hoverable'
      columns={columns}
      rows={rows}
      currentPage={currentPage}
      totalCount={totalCount}
      loading={loading}
      setPage={setPage}
      opts={opts}
      setOpts={setOptions}
      onRowClick={(item) => navigate(`/users/${item.id}`)}
    />
  )
}
